import { useEffect } from 'react'
import useHelpModeStore from '../../store/helpModeStore.js'

/**
 * Single global listener that tracks whether Alt/Option is held, to drive the
 * on-demand help "peek". Mounted once in AppLayout.
 *
 * Contract (verified against existing code):
 *  - NEVER preventDefault the Alt *keydown* — `altKey` is used as a modifier by
 *    comms/dm/Composer, qa/QABugForm, qa/QAOverlay, productions/DayCommentsThread.
 *  - Suppress only a *lone* Alt keyup (no combo in between) so Windows doesn't
 *    focus the browser menu bar.
 *  - Bail when the keydown originates in an editable element.
 *  - Clear on window blur / tab hide so a held Alt never gets stuck.
 */
export default function HelpModeKeyListener(): null {
  useEffect(() => {
    const setHeld = useHelpModeStore.getState().setPeekHeld
    let comboSinceAlt = false

    const isEditable = (el: EventTarget | null): boolean => {
      const node = el as HTMLElement | null
      const tag = node?.tagName
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || !!node?.isContentEditable
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Alt') {
        if (isEditable(e.target)) return
        comboSinceAlt = false
        setHeld(true)
        // no preventDefault — Alt must still reach other handlers
      } else if (e.altKey) {
        comboSinceAlt = true // Alt used as a modifier this press
      }
    }
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key !== 'Alt') return
      setHeld(false)
      if (!comboSinceAlt) e.preventDefault() // lone Alt → suppress menu-bar focus
      comboSinceAlt = false
    }
    const clear = () => setHeld(false)

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', clear)
    document.addEventListener('visibilitychange', clear)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', clear)
      document.removeEventListener('visibilitychange', clear)
    }
  }, [])
  return null
}
